import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../../../shared/components/Button.js';
import { listVendors, type VendorSummary } from '../../api/vendorsApi.js';
import {
  listPlanningCycles,
  currentCycleId,
  listBrandingTaxonomy,
  getVendorBrandingProfile,
  saveVendorBrandingProfile,
  type ChargeBasisOption,
  type PlanningCycle,
  type TaxonomyRow,
} from '../../api/vendorBrandingProfileApi.js';

/**
 * Vendor branding profile — the gate that comes before rate cards and work
 * orders. Ticking a branding type reveals its sub-types; only the ticked
 * sub-types may later be put on a work order for that vendor.
 *
 * Profiles are per financial year. Choosing a different year loads that year's
 * profile, so last year's selections stay visible and can be saved forward.
 */
export function VendorBrandingProfilePage(): ReactNode {
  const { token } = useAuth();
  // A work order refused for a missing FCM/RCM links here with the vendor and
  // year it named, so the screen opens on exactly that vendor-year.
  const [searchParams] = useSearchParams();
  const linkedVendorId = searchParams.get('vendorId') ?? '';
  const linkedCycleId = searchParams.get('planningCycleId') ?? '';
  const [vendors, setVendors] = useState<readonly VendorSummary[]>([]);
  const [cycles, setCycles] = useState<readonly PlanningCycle[]>([]);
  const [taxonomy, setTaxonomy] = useState<readonly TaxonomyRow[]>([]);
  const [vendorId, setVendorId] = useState(linkedVendorId);
  const [cycleId, setCycleId] = useState(linkedCycleId);
  const [types, setTypes] = useState<ReadonlySet<string>>(new Set());
  const [items, setItems] = useState<ReadonlySet<string>>(new Set());
  const [remarks, setRemarks] = useState('');
  const [chargeBasis, setChargeBasis] = useState('');
  const [chargeBasisOptions, setChargeBasisOptions] = useState<readonly ChargeBasisOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      listVendors(token).catch((e) => {
        if (alive) setError(`Vendors could not be loaded: ${(e as Error).message}`);
        return [] as readonly VendorSummary[];
      }),
      listPlanningCycles(token).catch((e) => {
        // An empty picker with no reason is indistinguishable from "no data".
        if (alive) setError(`Financial years could not be loaded: ${(e as Error).message}`);
        return [] as readonly PlanningCycle[];
      }),
      listBrandingTaxonomy(token).catch((e) => {
        if (alive) setError(`Branding types could not be loaded: ${(e as Error).message}`);
        return [] as readonly TaxonomyRow[];
      }),
    ])
      .then(([v, c, t]) => {
        if (!alive) return;
        setVendors(v);
        setCycles(c);
        setTaxonomy(t);
        // Current year first: the list is ordered newest-first by the API.
        setCycleId((prev) => prev || currentCycleId(c));
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  // Opened from a refused work order: load the vendor-year it named.
  useEffect(() => {
    if (linkedVendorId && linkedCycleId) void loadProfile(linkedVendorId, linkedCycleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedVendorId, linkedCycleId, token]);

  // Types with their sub-types, built from the flat taxonomy rows.
  const grouped = useMemo(() => {
    const map = new Map<string, { typeCode: string; typeName: string; items: TaxonomyRow[] }>();
    for (const r of taxonomy) {
      if (!map.has(r.brandingTypeId)) {
        map.set(r.brandingTypeId, { typeCode: r.typeCode, typeName: r.typeName, items: [] });
      }
      if (r.brandingItemId) map.get(r.brandingTypeId)!.items.push(r);
    }
    return [...map.entries()].sort((a, b) => a[1].typeCode.localeCompare(b[1].typeCode));
  }, [taxonomy]);

  const loadProfile = async (v: string, c: string) => {
    if (!v || !c) return;
    setLoading(true);
    setError(null);
    setStatus(null);
    try {
      const p = await getVendorBrandingProfile(v, c, token);
      setTypes(new Set(p.scope.map((s) => s.brandingTypeId)));
      setItems(new Set(p.scope.map((s) => s.brandingItemId).filter((x): x is string => !!x)));
      setRemarks(p.remarks ?? '');
      setChargeBasis(p.taxChargeBasisCode ?? '');
      setChargeBasisOptions(p.chargeBasisOptions);
      if (p.vendorBrandingProfileId === null) {
        setStatus('No profile saved for this year yet');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const pickVendor = (v: string) => { setVendorId(v); void loadProfile(v, cycleId); };
  const pickCycle = (c: string) => { setCycleId(c); void loadProfile(vendorId, c); };

  const toggleType = (typeId: string, itemIds: readonly string[]) => {
    setStatus(null);
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(typeId)) {
        next.delete(typeId);
        // Clearing a type must clear its sub-types, or a hidden tick would be
        // saved for a type the vendor no longer works on.
        setItems((old) => {
          const n = new Set(old);
          for (const id of itemIds) n.delete(id);
          return n;
        });
      } else {
        next.add(typeId);
      }
      return next;
    });
  };

  const toggleItem = (itemId: string) => {
    setStatus(null);
    setItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };

  const blocker = !vendorId ? 'Select a vendor'
    : !cycleId ? 'Select a financial year'
    : !chargeBasis ? 'Choose forward charge (FCM) or reverse charge (RCM) for this year'
    : types.size === 0 ? 'Tick at least one branding type'
    : null;

  const onSave = async () => {
    if (blocker) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const scope: Array<{ brandingTypeId: string; brandingItemId?: string }> = [];
      for (const [typeId, g] of grouped) {
        if (!types.has(typeId)) continue;
        const chosen = g.items.filter((i) => i.brandingItemId && items.has(i.brandingItemId));
        if (chosen.length === 0) {
          // Type ticked, no sub-type chosen — recorded as a type-level row.
          scope.push({ brandingTypeId: typeId });
        } else {
          for (const i of chosen) scope.push({ brandingTypeId: typeId, brandingItemId: i.brandingItemId! });
        }
      }
      await saveVendorBrandingProfile(
        { vendorId, planningCycleId: cycleId, remarks: remarks || undefined, taxChargeBasisCode: chargeBasis, scope },
        token,
      );
      setStatus('Profile saved');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main aria-label="vendor-branding-profile">
      <h1>Vendor branding profile</h1>
      <p>
        Tick the branding types this vendor works on, then the sub-types within
        them. Only ticked sub-types can be put on a work order for this vendor.
      </p>

      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}

      <div className="vbp-pickers">
        <label htmlFor="vbp-vendor">Vendor</label>
        <select
          id="vbp-vendor"
          value={vendorId}
          onChange={(e) => pickVendor(e.target.value)}
          data-testid="field-vendor"
        >
          <option value="">Select a vendor…</option>
          {vendors.map((v) => (
            <option key={v.vendorId} value={v.vendorId}>
              {v.vendorCode} — {v.vendorName}
            </option>
          ))}
        </select>

        <label htmlFor="vbp-cycle">Financial year</label>
        <select
          id="vbp-cycle"
          value={cycleId}
          onChange={(e) => pickCycle(e.target.value)}
          data-testid="field-cycle"
        >
          <option value="">Select a year…</option>
          {cycles.map((c) => (
            <option key={c.planningCycleId} value={c.planningCycleId}>
              {c.cycleName}
            </option>
          ))}
        </select>

        <label htmlFor="vbp-charge-basis">GST charge basis for this year</label>
        <select
          id="vbp-charge-basis"
          value={chargeBasis}
          onChange={(e) => { setStatus(null); setChargeBasis(e.target.value); }}
          disabled={!vendorId || !cycleId}
          data-testid="field-charge-basis"
        >
          <option value="">Select FCM or RCM…</option>
          {chargeBasisOptions.map((o) => (
            <option key={o.optionCode} value={o.optionCode}>{o.optionLabel}</option>
          ))}
        </select>
      </div>

      {vendorId && cycleId && !loading && !chargeBasis ? (
        <p role="alert" data-testid="charge-basis-missing">
          FCM / RCM is not recorded for this vendor for this financial year. No work
          order can be saved for this vendor in this year until it is.
        </p>
      ) : null}

      {loading ? <p>Loading profile…</p> : null}

      <section aria-label="branding-scope">
        {grouped.map(([typeId, g]) => (
          <fieldset key={typeId} data-testid={`type-${g.typeCode}`}>
            <legend>
              <label>
                <input
                  type="checkbox"
                  checked={types.has(typeId)}
                  onChange={() =>
                    toggleType(typeId, g.items.map((i) => i.brandingItemId!).filter(Boolean))
                  }
                  data-testid={`check-type-${g.typeCode}`}
                />
                {' '}{g.typeName}
              </label>
            </legend>
            {types.has(typeId) ? (
              <div data-testid={`items-${g.typeCode}`}>
                {g.items.length === 0 ? (
                  <p>No sub-types configured for this type.</p>
                ) : (
                  g.items.map((i) => (
                    <label key={i.brandingItemId!} style={{ display: 'block' }}>
                      <input
                        type="checkbox"
                        checked={items.has(i.brandingItemId!)}
                        onChange={() => toggleItem(i.brandingItemId!)}
                        data-testid={`check-item-${i.itemCode}`}
                      />
                      {' '}{i.itemName}
                    </label>
                  ))
                )}
              </div>
            ) : null}
          </fieldset>
        ))}
      </section>

      <label htmlFor="vbp-remarks">Remarks</label>
      <textarea
        id="vbp-remarks"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        data-testid="field-remarks"
      />

      {blocker ? <p role="status" data-testid="save-blocker">{blocker}</p> : null}
      <Button
        type="button"
        variant="primary"
        onClick={onSave}
        disabled={!!blocker || saving}
        title={blocker ?? undefined}
        data-testid="save-profile"
      >
        {saving ? 'Saving…' : 'Save profile'}
      </Button>
    </main>
  );
}
